import { Button } from '@edsn/pie-components';
import addIcon from '@iconify/icons-material-symbols/add';
import crossIcon from '@iconify/icons-material-symbols/close';
import { Fragment } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Divider } from '#pie/components/divider/Divider';
import { Icon } from '#pie/components/icon/Icon';
import { Stack } from '#pie/components/stack/Stack';
import {
  CustomerImpact,
  useGetRejectionCategory,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

export const rejectionReasonValidationSchema = z.object({
  rejections: z
    .array(
      z.object({
        rejectionCategoryId: z.string(),
        rejectionReasonId: z.string().nullable(),
      }),
    )
    .min(1)
    .optional(),
  customerImpact: z.enum([CustomerImpact.Yes, CustomerImpact.No]).optional(),
});

type RejectionReasonForm = z.infer<typeof rejectionReasonValidationSchema>;

const { FormDropdown } = typedFormFields<RejectionReasonForm>();

export const TicketRejectionReasonFields = () => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');
  const { control, watch, resetField } = useFormContext<RejectionReasonForm>();
  const { data, isLoading } = useGetRejectionCategory();

  const { remove, append, fields } = useFieldArray<RejectionReasonForm>({
    control,
    name: 'rejections',
  });

  const rejections = watch('rejections') || [];

  const hasCustomerImpact = rejections?.some((_, idx) => {
    const currentCategory = watch(`rejections.${idx}.rejectionCategoryId`);
    const currentReasonId = watch(`rejections.${idx}.rejectionReasonId`);

    const categoryReasons =
      data?.items.find((category) => category.id === currentCategory)
        ?.reasons || [];

    return categoryReasons.some(
      (reason) =>
        reason.id === currentReasonId && reason.customerImpactMandatory,
    );
  });

  return (
    <section>
      <Stack gap="sm">
        {fields.map(({ id }, idx) => {
          const currentCategory = watch(
            `rejections.${idx}.rejectionCategoryId`,
          );

          const categoryReasons =
            data?.items.find((category) => category.id === currentCategory)
              ?.reasons || [];

          /* istanbul ignore next -- @preserve */
          return (
            <Fragment key={id}>
              {idx !== 0 && <Divider className="my-3" />}
              <Stack direction="row" gap="sm">
                <div className="grid grow grid-cols-2 gap-4">
                  <FormDropdown
                    name={`rejections.${idx}.rejectionCategoryId`}
                    label={t('common.rejection_reason.category')}
                    fullWidth
                    options={data?.items.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={() => {
                      resetField(`rejections.${idx}.rejectionReasonId`);
                    }}
                    isLoading={isLoading}
                    className="basis-1/2"
                    rules={{ required: true }}
                  />
                  <FormDropdown
                    name={`rejections.${idx}.rejectionReasonId`}
                    label={t('common.rejection_reason.title')}
                    fullWidth
                    options={categoryReasons.map((reason) => ({
                      label: reason.name,
                      value: reason.id,
                    }))}
                    disabled={!watch(`rejections.${idx}.rejectionCategoryId`)}
                    isLoading={isLoading}
                    className="basis-1/2"
                  />
                </div>
                {fields.length > 1 && (
                  <Button
                    iconStart={
                      <Icon icon={crossIcon} className="text-content-error" />
                    }
                    size="sm"
                    action="tertiary"
                    emphasis="minimal"
                    onClick={() => remove(idx)}
                    className="self-end px-0"
                    aria-label={transtic('create.rejection_reason.remove')}
                  />
                )}
              </Stack>
            </Fragment>
          );
        })}
        {hasCustomerImpact && (
          <FormDropdown
            fullWidth
            label={transtic('customer_impact')}
            name={'customerImpact'}
            options={[
              { label: t('common.yes'), value: CustomerImpact.Yes },
              { label: t('common.no'), value: CustomerImpact.No },
            ]}
            rules={{ required: true }}
          />
        )}
        <Button
          className="self-start"
          action="primary"
          emphasis="minimal"
          iconStart={<Icon icon={addIcon} />}
          onClick={() =>
            append({ rejectionCategoryId: '', rejectionReasonId: '' })
          }
        >
          {transtic('rejection_reason_button')}
        </Button>
      </Stack>
    </section>
  );
};
